import { createSlice, nanoid } from '@reduxjs/toolkit';
import { parseColor } from '@printu/geom';
import { Prompt } from 'app/stores/editor/prompt';
import { projectSlice } from 'app/stores/project';
import { defaultColors, defaultSizes, initialColor } from 'app/stores/styles';
import { analytics } from 'app/utils/analytics';
import { settings } from 'app/utils/settingsStorage';
import { getColorScheme } from 'app/utils/varia';
const AvailableTools = [
    { id: 'arrow', alias: 'Arrow', defaults: {} },
    { id: 'line', alias: 'Line', defaults: {} },
    { id: 'polyline', alias: 'Polyline', icon: 'PolylineIcon', defaults: {} },
    { id: 'ellipse', alias: 'Ellipse', defaults: {} },
    { id: 'rect', alias: 'Rect', defaults: {} },
    {
        id: 'highlight',
        alias: 'Highlight',
        icon: 'HighlighterIcon',
        defaults: {},
    },
    { id: 'path', alias: 'Path', defaults: {} },
    { id: 'blur', alias: 'Blur', defaults: {} },
    { id: 'spot', alias: 'Spot', defaults: {} },
    { id: 'zoom', alias: 'Zoom', defaults: {} },
    { id: 'pin', alias: 'Pin', defaults: {} },
    { id: 'text', alias: 'Text', defaults: {} },
    {
        id: 'note',
        alias: 'Text',
        label: 'Note',
        overrides: { type: 'cloud' },
        defaults: {},
    },
    { id: 'image', alias: 'Image', defaults: {} },
];
const initalMarkerColors = {
    highlight: defaultColors[0],
};
const initialState = {
    config: null,
    locale: 'pl',
    startup: 'starting',
    startedAt: 0,
    savedAt: 0,
    colors: defaultColors,
    sizes: defaultSizes,
    consoleIsDirty: 0,
    availableControllers: ['image-crop', 'text-editor', 'image-annotate'],
    activeController: 'image-annotate',
    availableTools: AvailableTools,
    activeTool: AvailableTools[0].id,
    markerColor: Object.assign({ lastUsedColor: undefined, [AvailableTools[0].id]: initialColor }, initalMarkerColors),
    controllerData: {},
    toasts: [],
    TEMPORARY_settingsOpened: false,
};
export const editorSlice = createSlice({
    name: 'editor',
    initialState: () => {
        let colors = defaultColors;
        // load local palette
        const { colors: paletteStr } = settings().get();
        if (typeof paletteStr === 'string') {
            let hasValidPalette = true;
            try {
                const palette = JSON.parse(paletteStr);
                if (Array.isArray(palette)) {
                    colors = [
                        ...palette.filter(item => !!parseColor(item)),
                        ...defaultColors,
                    ].slice(0, defaultColors.length);
                }
                else {
                    hasValidPalette = false;
                }
            }
            catch (_) {
                hasValidPalette = false;
            }
            // remove palette
            if (!hasValidPalette) {
                settings().set('colors', undefined);
            }
        }
        const state = Object.assign(Object.assign({}, initialState), { colors, scheme: getColorScheme() });
        return state;
    },
    reducers: {
        startup: (state, action) => {
            state.startup = action.payload;
        },
        setScheme: (state, action) => {
            var _a;
            state.scheme = (_a = action.payload.scheme) !== null && _a !== void 0 ? _a : 'light';
        },
        setConfig: (state, action) => {
            state.config = action.payload.version;
        },
        setColorsPalette: (state, action) => {
            const { colors: newColors } = action.payload;
            state.colors = newColors;
            return state;
        },
        setController: (state, action) => {
            const { controller, data } = action.payload;
            if (state.activeController === controller) {
                return state;
            }
            state.activeController = controller;
            state.controllerData = data !== null && data !== void 0 ? data : {};
            return state;
        },
        setControllerData: (state, action) => {
            const { key, value } = action.payload;
            if (value) {
                state.controllerData[key] = value;
            }
            else {
                delete state.controllerData[key];
            }
            return state;
        },
        setTool: (state, action) => {
            const { toolId } = action.payload;
            const lcid = toolId === null || toolId === void 0 ? void 0 : toolId.toLowerCase();
            if (toolId && state.activeTool !== lcid) {
                state.activeTool = lcid;
            }
            else {
                delete state.activeTool;
            }
        },
        toolConfig: (state, action) => {
            const { id, patch } = action.payload;
            const toolIndex = state.availableTools.findIndex(tool => tool.id === id);
            if (toolIndex !== -1) {
                const tool = state.availableTools[toolIndex];
                tool.defaults = Object.assign(Object.assign({}, tool.defaults), patch);
            }
        },
        setDebugPoints: (state, action) => {
            const { points } = action.payload;
            if (points && points.length) {
                state.debugPoints = [...points];
            }
            else {
                delete state.debugPoints;
            }
            return state;
        },
        showBusy: (state, action) => {
            const { message, memo } = action.payload;
            state.busy = { id: nanoid(), message, memo };
        },
        hideBusyId: (state, action) => {
            var _a;
            if (((_a = state.busy) === null || _a === void 0 ? void 0 : _a.id) === action.payload.id) {
                delete state.busy;
            }
        },
        setNews: (state, action) => {
            var _a;
            state.news = (_a = action.payload) !== null && _a !== void 0 ? _a : [];
        },
        showToast: (state, action) => {
            state.toasts.push(action.payload);
        },
        popToast: state => {
            if (state.toasts.length) {
                state.toasts = state.toasts.slice(1);
            }
        },
        removeNews: (state, action) => {
            const { news } = state;
            if (!news || !news.length) {
                return;
            }
            const { ids } = action.payload;
            state.news = news.filter(item => ids.indexOf(item.id) === -1);
        },
        setRejections: (state, action) => {
            const { rejections } = action.payload;
            if (Array.isArray(rejections) && rejections.length) {
                // structuredClone
                state.rejections = [...(state.rejections || []), ...rejections];
            }
            else {
                delete state.rejections;
            }
            state.rejections = rejections;
        },
        setClipboardRender: (state, action) => {
            state.clipboardRender = action.payload;
        },
        TEMPORARYsetSettingsOpened: (state, action) => {
            const active = action.payload;
            if (active) {
                analytics({ action: 'Settings' });
            }
            state.TEMPORARY_settingsOpened = active;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(Prompt.show.pending, (state, action) => {
            if (state.prompt) {
                return state;
            }
            const { meta } = action;
            const { arg } = meta !== null && meta !== void 0 ? meta : {};
            state.prompt = Object.assign({ id: meta.requestId }, arg);
            return state;
        });
        builder.addCase(projectSlice.actions.setColor.type, (state, action) => {
            if (configGet('saveColorPerTool', false)) {
                const { color } = action.payload;
                state.markerColor.lastUsedColor = color;
                state.markerColor[state.activeTool] = color;
            }
            return state;
        });
        builder.addMatcher(action => action.type === Prompt.show.fulfilled.type ||
            action.type === Prompt.show.rejected.type, (state, action) => {
            var _a;
            if (action.meta.requestId === ((_a = state.prompt) === null || _a === void 0 ? void 0 : _a.id)) {
                delete state.prompt;
            }
            return state;
        });
    },
});
